import React, { createContext, useContext, useState, useCallback } from 'react';

interface BannerCarouselContextType {
  activeSlides: string[];
  registerSlide: (name: string) => void;
  unregisterSlide: (name: string) => void;
  // slideHeights is needed to allow smooth height transitions in the container
  // when navigating between slides of different heights.
  slideHeights: Record<string, number>;
  setSlideHeight: (name: string, height: number) => void;
}

const BannerCarouselContext = createContext<BannerCarouselContextType | undefined>(undefined);

export const BannerCarouselProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [activeSlides, setActiveSlides] = useState<string[]>([]);
  const [slideHeights, setSlideHeights] = useState<Record<string, number>>({});

  const registerSlide = useCallback((name: string) => {
    setActiveSlides((prev) => {
      if (prev.includes(name)) return prev;
      return [...prev, name];
    });
  }, []);

  const unregisterSlide = useCallback((name: string) => {
    setActiveSlides((prev) => prev.filter((slide) => slide !== name));
  }, []);

  const setSlideHeight = useCallback((name: string, height: number) => {
    setSlideHeights((prev) => {
      if (prev[name] === height) return prev;
      return { ...prev, [name]: height };
    });
  }, []);

  return (
    <BannerCarouselContext.Provider
      value={{ activeSlides, registerSlide, unregisterSlide, slideHeights, setSlideHeight }}
    >
      {children}
    </BannerCarouselContext.Provider>
  );
};

export const useBannerCarouselContext = () => {
  const context = useContext(BannerCarouselContext);
  if (!context) {
    throw new Error('useBannerCarouselContext must be used within a BannerCarouselProvider');
  }
  return context;
};
